import { Box, Center, Flex, Loader, Text, Transition } from '@mantine/core';
import { useCurrentClientIfAvailable } from 'src/clients/context/CurrentClientProvider';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { useAppContainerContext } from '../AppContainerContext';
import { useChatScroll } from '../chat/hooks/useChatScroll';
import { useSiesta } from '../SiestaProvider';
import { SteeringActionContent } from '../steering/SteeringActionContent';
import { SiestaAction, SiestaActionType } from '../types';
import { useAppContainer } from '../useAppContainer';
import { AIActionSet } from './AIActionSet';
import { AskSiestaInput } from './AskSiestaInput';
import { ChatEmptyState } from './ChatEmptyState';
import { ChatScrollBlur } from './ChatScrollBlur';
import { CtaAdditionalAI } from './CtaAdditionalAI';
import { DisplacedChatBanner } from './DisplacedChatBanner';
import classes from './SiestaBar.module.css';
import { SiestaBusyGlow } from './SiestaBusyGlow';
import { SiestaBarLayout } from './SiestaBarLayout';
import { SiestaBarMessages } from './SiestaBarMessages';
import { useChatEmptyMessage } from './useChatEmptyMessage';
import { useRecordEditAction } from './useRecordEditAction';
import { useSuggestedActions } from './useSuggestedActions';

export function SiestaBar() {
  const { siestaMode, setSiestaMode, siestaBusyGlowTop } = useAppContainer();
  const { contentMeta, conversationId } = useAppContainerContext();
  const emptyMessage = useChatEmptyMessage();
  const { clientId } = useCurrentClientIfAvailable();
  const { recordAction } = useRecordEditAction();

  const {
    messages,
    latestIncomingId,
    currentChatState,
    sendUserMessage,
    discardChat,
    resetIfDisplaced,
    previousChatContext,
    goBack,
    markActionUsed,
    usedActionIds,
  } = useSiesta();

  const isProcessing = currentChatState !== null;

  const lastMessage = messages[messages.length - 1];
  const contentSignal = lastMessage?.answerContent.length ?? 0;
  const { scrollRef, handleScroll, forceScrollToBottom } = useChatScroll(
    messages.length,
    contentSignal,
  );

  const { actions: siestaActions, isLoading: isLoadingActions } = useSuggestedActions({
    documentId: contentMeta?.entityId,
    documentContent: contentMeta?.metadata?.documentContent,
    clientId,
    conversationId,
  });

  const visibleActions = siestaActions.filter(a => !usedActionIds.has(a.id));

  const handleActionClick = async (action: SiestaAction) => {
    if (action.type !== SiestaActionType.CHAT) return;

    const activeConversationId = resetIfDisplaced();
    markActionUsed(action.id);
    setSiestaMode('chat');

    await sendUserMessage(action.prompt ?? action.label, clientId ?? null, activeConversationId);
    forceScrollToBottom();

    if (contentMeta?.entityId && clientId) {
      recordAction({
        documentId: contentMeta.entityId,
        actionText: action.label,
        source: 'SUGGESTED',
        conversationId: activeConversationId,
        clientId,
      });
    }
  };

  const handleInputSubmit = async (message: string) => {
    const activeConversationId = resetIfDisplaced();
    setSiestaMode('chat');
    await sendUserMessage(message, clientId ?? null, activeConversationId);
    forceScrollToBottom();
  };

  const handleInputFocus = () => {
    setSiestaMode('chat');
  };

  const handleBackToActions = () => {
    discardChat();
    setSiestaMode('action');
  };

  return (
    <Box pos="relative" h="100%">
      <Transition mounted={siestaMode === 'busy'} transition="fade" duration={200} keepMounted>
        {styles => (
          <Box
            className={classes.busyContainer}
            px="xxxl"
            pos="absolute"
            top="xl"
            style={{ ...styles, overflow: 'hidden', inset: 0 }}
          >
            <SiestaBusyGlow top={siestaBusyGlowTop ?? '50%'} />
            <Text size="textXl" fw={FONT_WEIGHTS.semibold} c="#333" className={classes.busyText}>
              {'Siesta is busy,\nfinish the action to turn it back on'}
            </Text>
          </Box>
        )}
      </Transition>

      <Transition mounted={siestaMode === 'chat'} transition="fade" duration={200}>
        {styles => (
          <Flex
            direction="column"
            h="100%"
            pos="absolute"
            bg="white"
            px="xl"
            pb="xxxl"
            style={{ ...styles, inset: 0 }}
          >
            <CtaAdditionalAI onClick={handleBackToActions} disabled={isProcessing} />
            <ChatScrollBlur />

            <Box
              ref={scrollRef}
              onScroll={handleScroll}
              flex={1}
              pt="xxxxl"
              style={{ overflow: 'auto' }}
            >
              {messages.length === 0 ? (
                <ChatEmptyState title={emptyMessage.title} subtitle={emptyMessage.subtitle} />
              ) : (
                <SiestaBarMessages
                  messages={messages}
                  latestIncomingId={latestIncomingId ?? undefined}
                  isStreaming={isProcessing}
                />
              )}
            </Box>

            <DisplacedChatBanner previousChatContext={previousChatContext} onGoBack={goBack} />
            <AskSiestaInput
              variant="brand"
              onSubmit={handleInputSubmit}
              autoFocus
              submitDisabled={isProcessing}
            />
          </Flex>
        )}
      </Transition>

      <Transition mounted={siestaMode === 'action'} transition="fade" duration={200} keepMounted>
        {styles => (
          <Box pos="absolute" style={{ ...styles, inset: 0 }}>
            <SiestaBarLayout>
              <Flex h="100%" direction="column" justify="space-between">
                <ActionModeContent
                  isLoadingActions={isLoadingActions}
                  visibleActions={visibleActions}
                  onActionClick={handleActionClick}
                />
                <AskSiestaInput
                  variant="light"
                  onSubmit={handleInputSubmit}
                  onFocus={handleInputFocus}
                />
              </Flex>
            </SiestaBarLayout>
          </Box>
        )}
      </Transition>
    </Box>
  );
}

function ActionModeContent({
  isLoadingActions,
  visibleActions,
  onActionClick,
}: {
  isLoadingActions: boolean;
  visibleActions: SiestaAction[];
  onActionClick: (action: SiestaAction) => void;
}) {
  const { flagEnabledSiestaSteering } = useFlags();

  if (flagEnabledSiestaSteering) {
    return (
      <Flex flex={1} direction="column" style={{ minHeight: 0 }}>
        <SteeringActionContent suggestedActions={visibleActions} onActionClick={onActionClick} />
      </Flex>
    );
  }

  if (isLoadingActions) {
    return (
      <Center flex={1}>
        <Loader color="white" />;
      </Center>
    );
  }

  return (
    <Flex flex={1} align="center" justify="center">
      <AIActionSet actions={visibleActions} onActionClick={onActionClick} />
    </Flex>
  );
}
